import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack, Close, Replay } from "@mui/icons-material";
import {
  ACCENT,
  ACCENT_DARK,
  StageBar,
  StatusChip,
  formatDate,
  formatWeight,
  isRejected,
  isVideoUrl,
  parseImages,
} from "./orderUi";

const OrderDetailsDialog = ({ order, onClose, onReorder }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!order) return null;

  const images = parseImages(order.order_images);
  const rows = [
    ["Order date", formatDate(order.orderdate)],
    ["Expected delivery", formatDate(order.deliverydate)],
    ["Last update", formatDate(order.work_status_date)],
    ["Delivered on", formatDate(order.delivereddate)],
    ["Item", order.itemtype_name],
    ["Design", order.design_name],
    ["Purity", order.purity_name],
    ["Gross weight", formatWeight(order.weight)],
    ["Pcs", order.pcs],
    ["Size", order.size_len],
  ].filter(([, value]) => value);

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : "12px" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: "1px solid #eee",
        }}
      >
        <IconButton onClick={onClose} aria-label="close">
          {fullScreen ? <ArrowBack /> : <Close />}
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }} noWrap>
          {order.storeorderno}
        </Typography>
        <Box sx={{ pr: 1 }}>
          <StatusChip status={order.work_status} size="medium" />
        </Box>
      </Box>

      <DialogContent sx={{ px: 2, py: 2 }}>
        {/* Progress */}
        <StageBar status={order.work_status} showLabels />
        {isRejected(order.work_status) && order.rejection_message && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: "8px",
              bgcolor: "#fdecea",
              color: "#c62828",
            }}
          >
            {order.rejection_message}
          </Typography>
        )}

        {/* Photos: swipe on mobile, tap to open full size */}
        {images.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              mt: 2,
              mx: -2,
              px: 2,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {images.map((url) =>
              isVideoUrl(url) ? (
                <Box
                  key={url}
                  component="video"
                  src={url}
                  controls
                  sx={{
                    height: 220,
                    borderRadius: "8px",
                    scrollSnapAlign: "start",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  key={url}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ flexShrink: 0, scrollSnapAlign: "start" }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={order.storeorderno}
                    sx={{
                      height: 220,
                      maxWidth: "80vw",
                      borderRadius: "8px",
                      objectFit: "cover",
                      display: "block",
                      bgcolor: "#f3f3f3",
                    }}
                  />
                </Box>
              ),
            )}
          </Box>
        )}

        {/* Details */}
        <Box sx={{ mt: 2 }}>
          {rows.map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                py: 1,
                borderBottom: "1px solid #f2f2f2",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={500} textAlign="right">
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {order.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Notes
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#f7f7f9",
              }}
            >
              {order.description}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {onReorder && (
        <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<Replay />}
            onClick={() => onReorder(order)}
            sx={{
              bgcolor: ACCENT,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: ACCENT_DARK },
            }}
          >
            Reorder this
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default OrderDetailsDialog;
